import type { AdapterCapabilities } from '../ai/skills/skill-platform-types';
import { round2 } from '../../utils/number/number-utils';

export type MediaPlanDefaultsLineItem = {
  planned_unit_cost: number;
  unit_price_type_name?: string;
  geo?: string;
  tactic_name?: string;
  channel_name?: string;
};

export type ZohoMediaPlanIntent = {
  geo?: string[];
  audience?: string[];
  description?: string;
  notes?: string;
};

export type MediaPlanDefaultsSelection = {
  lineItemType?: string;
  budgetType?: string;
  biddingStrategy?: string;
  geo?: string[];
  audienceHints?: string[];
  bid?: number;
};

export type MediaPlanAmbiguityField = 'lineItemType' | 'geo' | 'audience' | 'budgetType';

export type MediaPlanAmbiguity = {
  field: MediaPlanAmbiguityField;
  candidates: Array<{ id: string; name: string }>;
  reason: string;
};

export type MediaPlanDefaultsResult = {
  defaults: MediaPlanDefaultsSelection;
  ambiguities: MediaPlanAmbiguity[];
};

export function resolveMediaPlanDefaults(
  lineItem: MediaPlanDefaultsLineItem,
  zohoIntent: ZohoMediaPlanIntent,
  capabilities: AdapterCapabilities
): MediaPlanDefaultsResult {
  const defaults: MediaPlanDefaultsSelection = {};
  const ambiguities: MediaPlanAmbiguity[] = [];

  // Two-tier match: structured fields (channel + tactic) are authoritative when they
  // pin a unique type. Only fall back to noisy free-text (description/notes/audience/geo)
  // when the structured signal is ambiguous or empty.
  const structuredCorpus = [lineItem.channel_name, lineItem.tactic_name]
    .filter((s): s is string => !!s)
    .join(' ');
  const fullCorpus = [
    structuredCorpus,
    zohoIntent.description,
    zohoIntent.notes,
    ...(zohoIntent.audience ?? []),
    ...(zohoIntent.geo ?? [])
  ]
    .filter((s): s is string => !!s)
    .join(' ');

  const matchedType =
    matchLineItemType(
      structuredCorpus,
      capabilities.lineItemTypes,
      capabilities.lineItemTypeAliases
    ) ??
    matchLineItemType(fullCorpus, capabilities.lineItemTypes, capabilities.lineItemTypeAliases);

  if (matchedType) {
    defaults.lineItemType = matchedType;
  } else {
    ambiguities.push({
      field: 'lineItemType',
      candidates: capabilities.lineItemTypes.map(t => ({ id: t, name: t })),
      reason: fullCorpus
        ? `Could not infer a unique line item type from tactic + Zoho campaign details`
        : 'No tactic or Zoho text — pick a line item type'
    });
  }

  // Pre-select the DSP's starting bidding strategy when it declares one (e.g. Google Ads SEM
  // always starts on MAXIMIZE_CLICKS). The user can still change it on the card.
  if (capabilities.defaultBiddingStrategy) {
    defaults.biddingStrategy = capabilities.defaultBiddingStrategy;
  }

  const geo = unionGeo(lineItem.geo, zohoIntent.geo);
  if (geo.length > 0) defaults.geo = geo;

  if (zohoIntent.audience && zohoIntent.audience.length > 0) {
    defaults.audienceHints = zohoIntent.audience;
  }

  if (lineItem.unit_price_type_name === 'CPM') {
    defaults.bid = round2(lineItem.planned_unit_cost * 1000);
  }

  return { defaults, ambiguities };
}

function matchLineItemType(
  corpus: string,
  types: string[],
  aliases?: Record<string, string[]>
): string | undefined {
  if (!corpus) return undefined;
  const lower = corpus.toLowerCase();
  const matches = types.filter(t => {
    const needles = [t, ...(aliases?.[t] ?? [])].map(n => n.toLowerCase());
    return needles.some(n => lower.includes(n));
  });
  return matches.length === 1 ? matches[0] : undefined;
}

function unionGeo(lineItemGeo: string | undefined, zohoGeo: string[] | undefined): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const g of [lineItemGeo, ...(zohoGeo ?? [])]) {
    if (!g) continue;
    if (!seen.has(g)) {
      seen.add(g);
      out.push(g);
    }
  }
  return out;
}
